import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useShopifyProducts } from "@/context/ShopifyProductsContext";

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const ALL_PAGES = [
  { label: "Shop All Products", to: "/shop", icon: "ri-store-2-line", desc: "Browse backup power systems" },
  { label: "Compare Systems", to: "/compare-backup-power-systems", icon: "ri-equalizer-2-line", desc: "Solar vs Gas vs Whole-Home" },
  { label: "Side-by-Side Compare", to: "/compare", icon: "ri-layout-column-line", desc: "Compare specific products" },
  { label: "FAQ", to: "/faq", icon: "ri-question-answer-line", desc: "Shipping, returns, warranty answers" },
  { label: "Installation Guides", to: "/installation-guides", icon: "ri-tools-line", desc: "Step-by-step setup guides" },
  { label: "Warranty Information", to: "/warranty", icon: "ri-shield-check-line", desc: "Coverage & claim process" },
  { label: "MAP Policy", to: "/map-policy", icon: "ri-price-tag-3-line", desc: "Minimum advertised price info" },
  { label: "About Us", to: "/about", icon: "ri-information-line", desc: "Our story and mission" },
  { label: "Contact Us", to: "/contact", icon: "ri-mail-line", desc: "Reach our support team" },
];

const QUICK_LINKS = ALL_PAGES.slice(0, 5);

export default function SearchOverlay({ isOpen, onClose }: SearchOverlayProps) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { products } = useShopifyProducts();

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  const q = query.trim().toLowerCase();

  const productResults = q.length > 1
    ? products
        .filter((p) => {
          const haystack = [
            p.name,
            p.brand,
            p.category,
            ...(p.highlights || []),
            p.badge || "",
          ]
            .join(" ")
            .toLowerCase();
          return q.split(" ").every((word) => haystack.includes(word));
        })
        .slice(0, 6)
    : [];

  const fullMatchCount = q.length > 1
    ? products.filter((p) => {
        const haystack = [p.name, p.brand, p.category, ...(p.highlights || []), p.badge || ""]
          .join(" ")
          .toLowerCase();
        return q.split(" ").every((word) => haystack.includes(word));
      }).length
    : 0;

  const pageResults = q.length > 0
    ? ALL_PAGES.filter((p) => p.label.toLowerCase().includes(q) || p.desc.toLowerCase().includes(q))
    : [];

  const hasResults = productResults.length > 0 || pageResults.length > 0;

  const handleProductClick = (id: string) => {
    navigate(`/shop/${id}`);
    onClose();
  };

  const handlePageClick = () => {
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (q.length > 0) {
      navigate(`/shop?q=${encodeURIComponent(q)}`);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex flex-col items-center pt-16 md:pt-24 px-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl overflow-hidden">
        {/* Search input */}
        <form onSubmit={handleSubmit} className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <div className="w-5 h-5 flex items-center justify-center text-gray-400 shrink-0">
            <i className="ri-search-line text-lg"></i>
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products, pages, guides..."
            className="flex-1 text-sm text-gray-900 placeholder-gray-400 bg-transparent focus:outline-none"
          />
          {query.length > 0 && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer transition-colors rounded-full hover:bg-gray-100"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-xs font-semibold text-gray-400 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            Esc
          </button>
        </form>

        {/* Results area */}
        <div className="max-h-[60vh] overflow-y-auto">
          {/* No query — show quick links */}
          {q.length === 0 && (
            <div className="p-4">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest px-2 mb-3">Quick Links</p>
              <div className="space-y-0.5">
                {QUICK_LINKS.map((page) => (
                  <Link
                    key={page.to}
                    to={page.to}
                    onClick={handlePageClick}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors group cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg shrink-0">
                      <i className={`${page.icon} text-amber-500 text-sm`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 group-hover:text-amber-600 transition-colors">{page.label}</p>
                      <p className="text-xs text-gray-400 truncate">{page.desc}</p>
                    </div>
                    <div className="w-4 h-4 flex items-center justify-center text-gray-300 group-hover:text-amber-400 transition-colors ml-auto shrink-0">
                      <i className="ri-arrow-right-s-line text-base"></i>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Has query — no results */}
          {q.length > 0 && !hasResults && (
            <div className="py-10 text-center">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 text-gray-200">
                <i className="ri-search-eye-line text-3xl"></i>
              </div>
              <p className="text-gray-500 text-sm font-medium">No results for &ldquo;{query}&rdquo;</p>
              <p className="text-gray-400 text-xs mt-1">Try a product name, brand, or page name</p>
            </div>
          )}

          {/* Product results */}
          {productResults.length > 0 && (
            <div className="p-4 border-b border-gray-50">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest px-2 mb-3">Products</p>
              <div className="space-y-0.5">
                {productResults.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleProductClick(product.id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left cursor-pointer group"
                  >
                    <div className="w-10 h-10 shrink-0 bg-gray-50 rounded-lg overflow-hidden border border-gray-100">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-contain p-1"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 group-hover:text-amber-600 transition-colors truncate">
                        {product.name}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <p className="text-xs text-gray-400">{product.brand} · ${product.price.toLocaleString()}</p>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                          product.category === "solar"
                            ? "bg-emerald-50 text-emerald-600"
                            : product.category === "gas"
                            ? "bg-orange-50 text-orange-600"
                            : "bg-blue-50 text-blue-600"
                        }`}>
                          {product.category === "solar" ? "Solar" : product.category === "gas" ? "Gas" : "Whole-Home"}
                        </span>
                      </div>
                    </div>
                    <div className={`text-xs font-bold whitespace-nowrap shrink-0 ${product.inStock ? "text-emerald-500" : "text-red-400"}`}>
                      {product.inStock ? "In Stock" : "Out of Stock"}
                    </div>
                  </button>
                ))}
              </div>
              {fullMatchCount > 6 && (
                <button
                  onClick={() => { navigate(`/shop?q=${encodeURIComponent(query)}`); onClose(); }}
                  className="mt-2 w-full text-center text-xs text-amber-600 hover:text-amber-500 font-semibold py-2 cursor-pointer transition-colors"
                >
                  View all {fullMatchCount} results in shop →
                </button>
              )}
            </div>
          )}

          {/* Page results */}
          {pageResults.length > 0 && (
            <div className="p-4">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest px-2 mb-3">Pages</p>
              <div className="space-y-0.5">
                {pageResults.map((page) => (
                  <Link
                    key={page.to}
                    to={page.to}
                    onClick={handlePageClick}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors group cursor-pointer"
                  >
                    <div className="w-7 h-7 flex items-center justify-center bg-gray-100 rounded-lg shrink-0">
                      <i className={`${page.icon} text-gray-500 text-sm`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-700 group-hover:text-amber-600 transition-colors">{page.label}</p>
                      <p className="text-xs text-gray-400 truncate">{page.desc}</p>
                    </div>
                    <div className="w-4 h-4 flex items-center justify-center text-gray-300 group-hover:text-amber-400 transition-colors ml-auto shrink-0">
                      <i className="ri-arrow-right-s-line text-base"></i>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
          <p className="text-xs text-gray-400">
            {q.length > 1 && fullMatchCount > 0 ? (
              <>
                <span className="font-semibold text-amber-600">{fullMatchCount}</span> matches found
              </>
            ) : (
              <>
                <span className="font-semibold">{products.length}</span> products available
              </>
            )}
          </p>
          <p className="text-xs text-gray-400">
            Press <span className="font-semibold text-gray-500">Enter</span> to see all results in shop
          </p>
        </div>
      </div>
    </div>
  );
}
